// Setup › Finance › Access (`/c/{slug}/setup/finance`; 05 §finance, M5-03 `financeAccess.*`): who
// holds `finance_admin` (every finance write: pay, revenue, clients, categories, scenarios, the
// plan) and `seat#comp_viewer` (reads one seat's pay and revenue). Only the owner grants and
// revokes them, themselves included: owning the company reads every figure but changes none
// until the owner is a finance admin too. Calls on load: the access list and the people (names
// and the picker); the seats for a pay-viewer grant.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  EmptyState,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRoundIcon, PlusIcon, ShieldCheckIcon, UserXIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { peopleQuery } from "../queries";
import { Facts, LoadError, Loading, Section } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import { useSession } from "../use-feature";
import {
  FinancePage,
  FormDialog,
  FormField,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { financeKeys } from "./finance-queries";

type Grant = Awaited<ReturnType<Api["client"]["financeAccess"]["list"]>>["grants"][number];
type Relation = Grant["relation"];

const RELATION_LABELS: Record<Relation, string> = {
  finance_admin: "Finance admin",
  comp_viewer: "Pay viewer",
};

export function FinanceAccessScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const qc = useQueryClient();
  const { me } = useSession();
  const myMembership =
    me.memberships.find((m) => m.company_id === company.id)?.membership_id ?? null;
  const standing = useFinanceStanding(api, company);
  const people = useInfiniteQuery(peopleQuery(api, company.id));
  const names = useOrgNames(api, company);
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState<Grant | null>(null);
  const persons = (people.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (p) => p.kind === "person" && p.status === "active",
  );
  // The owner may be missing from a people page that has not loaded; they are always grantable.
  const choices = [
    ...(myMembership && !persons.some((p) => p.id === myMembership)
      ? [{ id: myMembership, display_name: `${me.user.name} (you)` }]
      : []),
    ...persons.map((p) => ({
      id: p.id,
      display_name: p.id === myMembership ? `${p.display_name} (you)` : p.display_name,
    })),
  ];
  const personName = (id: string) =>
    choices.find((p) => p.id === id)?.display_name ?? `Member …${id.slice(-4)}`;
  const invalidate = () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
  const grantSelf = useMutation({
    mutationFn: () =>
      api.client.financeAccess.grant({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: { relation: "finance_admin", membership_id: myMembership ?? "" },
      }),
    onSuccess: invalidate,
  });
  const revoke = useMutation({
    mutationFn: (g: Grant) =>
      api.client.financeAccess.revoke({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: { relation: g.relation, membership_id: g.membership_id, seat_id: g.seat_id },
      }),
    onSuccess: invalidate,
  });
  const grants = standing.query.data?.grants ?? [];

  return (
    <FinancePage
      company={company}
      where="setup"
      title="Finance access"
      icon={ShieldCheckIcon}
      actions={
        standing.owner ? (
          <Button size="sm" onClick={() => setGranting(true)}>
            <PlusIcon aria-hidden="true" />
            Grant access
          </Button>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading finance access" rows={3} />
      ) : standing.query.isError ? (
        <LoadError what="Finance access" error={standing.query.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <Facts
            items={[
              { label: "You own the company", value: standing.owner ? "Yes" : "No" },
              {
                label: "Finance admin",
                value: standing.admin ? "Yes: you change finance records" : "No",
              },
              {
                label: "Pay you read",
                value: standing.restricted
                  ? "Every seat"
                  : standing.compSeats.size
                    ? [...standing.compSeats].map((s) => names.seatName(s)).join(", ")
                    : "None",
              },
            ]}
          />
          {standing.owner && !standing.admin && myMembership ? (
            <Notice
              tone="info"
              icon={ShieldCheckIcon}
              data-slot="grant-self"
              title="You read every figure but change none yet"
              action={
                <Button size="sm" disabled={grantSelf.isPending} onClick={() => grantSelf.mutate()}>
                  {grantSelf.isPending ? "Granting…" : "Make me finance admin"}
                </Button>
              }
            >
              Finance writes need finance admin, the owner included, so that each one is a decision
              on record.
            </Notice>
          ) : null}
          <Section id="finance-grants" title="Grants">
            {!standing.owner &&
            !standing.admin &&
            !company.roles.includes("admin") &&
            !company.roles.includes("agency_operator") ? (
              <p className="text-sm text-muted-foreground">
                Only the owner, admins and finance admins see the grants.
              </p>
            ) : grants.length === 0 ? (
              <EmptyState
                variant="card"
                icon={KeyRoundIcon}
                title="No finance access granted"
                description="Grant finance admin to the people who keep the books, and pay viewer for a seat to someone who must see that seat's pay."
              />
            ) : (
              <Table data-slot="finance-grants">
                <TableCaption>
                  Each grant is recorded (access.granted / access.revoked).
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Person</TableHead>
                    <TableHead scope="col">Access</TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants.map((g) => (
                    <TableRow
                      key={`${g.relation}-${g.membership_id}-${g.seat_id ?? ""}`}
                      className="group/row"
                      data-grant={g.relation}
                    >
                      <TableHead scope="row" className="font-normal">
                        {personName(g.membership_id)}
                      </TableHead>
                      <TableCell>
                        <Badge variant={g.relation === "finance_admin" ? "accent" : "info"}>
                          {RELATION_LABELS[g.relation]}
                        </Badge>
                        {g.seat_id ? (
                          <span className="ml-2 text-sm text-muted-foreground">
                            {names.seatName(g.seat_id)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        {standing.owner ? (
                          <RowActions>
                            <RowAction
                              icon={UserXIcon}
                              label={`Revoke ${RELATION_LABELS[g.relation]} from ${personName(g.membership_id)}`}
                              tip="Revoke"
                              className="hover:text-danger-text"
                              onClick={() => setRevoking(g)}
                            />
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        </div>
      )}
      {granting ? (
        <GrantDialog
          api={api}
          company={company}
          people={choices}
          seats={names.seats}
          onClose={() => setGranting(false)}
        />
      ) : null}
      <AlertDialog open={revoking !== null} onOpenChange={(o) => (o ? null : setRevoking(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this access?</AlertDialogTitle>
            <AlertDialogDescription>
              {revoking
                ? `${personName(revoking.membership_id)} loses ${RELATION_LABELS[revoking.relation].toLowerCase()}${
                    revoking.seat_id ? ` on ${names.seatName(revoking.seat_id)}` : ""
                  } at once.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revoking) revoke.mutate(revoking);
                setRevoking(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePage>
  );
}

function GrantDialog({
  api,
  company,
  people,
  seats,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  people: { id: string; display_name: string }[];
  seats: { id: string; title: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [person, setPerson] = useState(people[0]?.id ?? "");
  const [relation, setRelation] = useState<Relation>("finance_admin");
  const [seat, setSeat] = useState(seats[0]?.id ?? "");
  const ids = { person: useId(), relation: useId(), seat: useId() };
  const grant = useMutation({
    mutationFn: () =>
      api.client.financeAccess.grant({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          relation,
          membership_id: person,
          ...(relation === "comp_viewer" ? { seat_id: seat } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Grant finance access"
      description="Finance admin changes every finance record. Pay viewer reads one seat's pay and revenue."
      submitLabel="Grant"
      pending={grant.isPending}
      error={grant.error}
      canSubmit={Boolean(person) && (relation === "finance_admin" || Boolean(seat))}
      onSubmit={() => grant.mutate()}
    >
      <FormField id={ids.person} label="Person">
        <NativeSelect id={ids.person} value={person} onChange={(e) => setPerson(e.target.value)}>
          {people.map((p) => (
            <NativeSelectOption key={p.id} value={p.id}>
              {p.display_name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </FormField>
      <FormField id={ids.relation} label="Access">
        <NativeSelect
          id={ids.relation}
          value={relation}
          onChange={(e) => setRelation(e.target.value as Relation)}
        >
          <NativeSelectOption value="finance_admin">
            {RELATION_LABELS.finance_admin}
          </NativeSelectOption>
          <NativeSelectOption value="comp_viewer">
            {RELATION_LABELS.comp_viewer} (one seat)
          </NativeSelectOption>
        </NativeSelect>
      </FormField>
      {relation === "comp_viewer" ? (
        <FormField id={ids.seat} label="Seat">
          <NativeSelect id={ids.seat} value={seat} onChange={(e) => setSeat(e.target.value)}>
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
      ) : null}
    </FormDialog>
  );
}
